import { RealmId } from "./realm-id";
import type { WeightedRealm, NonEmptyArray } from "./realmsolver-config";

/**
 * Computes a hash score for weighted rendezvous hashing.
 *
 * Uses realm hash as seed for key hash, ensuring each realm produces
 * an independent hash distribution for the same key.
 *
 * Formula: score = -log(hash) / weight
 * LOWEST score wins. Higher weight means lower score, more likely to win.
 */
const computeScore = (key: string, realm: string, weight: number): number => {
  // First hash the realm to get a seed
  let seed = 0x811c9dc5;
  for (let i = 0; i < realm.length; i++) {
    seed ^= realm.charCodeAt(i);
    seed = Math.imul(seed, 0x01000193);
  }

  // Now hash the key using the realm-derived seed
  // This makes each realm's hash function independent
  let hash = seed;
  for (let i = 0; i < key.length; i++) {
    hash ^= key.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  hash = hash >>> 0;

  // Normalize to (0, 1] - avoid 0 for log
  const normalized = (hash + 1) / 0x100000000;

  // -log(u) / weight: lower score wins, higher weight = lower score
  return -Math.log(normalized) / weight;
};

/**
 * Rendezvous (Highest Random Weight) hashing for consistent realm selection.
 *
 * Properties:
 * - Deterministic: same key always maps to same realm (given same config)
 * - Minimal disruption: adding/removing realms only affects keys that must move
 * - Weighted: realms with higher weights receive proportionally more keys
 *
 * @param key - The consistent key to hash (e.g., "customer:ACME")
 * @param realms - Non-empty array of weighted realms
 * @returns The selected RealmId
 */
export const rendezvousSelect = (key: string, realms: NonEmptyArray<WeightedRealm>): RealmId => {
  // Destructuring a NonEmptyArray gives `first: WeightedRealm` and `rest: WeightedRealm[]`
  // without a numeric index, avoiding the spurious `| undefined` from
  // `noUncheckedIndexedAccess` that would otherwise force an unreachable guard.
  const [first, ...rest] = realms;
  let bestRealm = first.realm;
  let bestScore = computeScore(key, RealmId.toString(first.realm), first.weight);

  for (const item of rest) {
    const score = computeScore(key, RealmId.toString(item.realm), item.weight);
    // Lowest score wins (standard HRW with -log formula)
    if (score < bestScore) {
      bestScore = score;
      bestRealm = item.realm;
    }
  }

  return bestRealm;
};
